import type { FC } from 'react';

import customLocalStorage from '@crehana/utils/customLocalStorage';
import { clsx } from 'clsx';

import SwitchNotificationUsers from '../../pages/AssignUsersByCourse/components/switch_notification_users';
import { useExcelImporterStepperContext } from '../ExcelImporterStepper/context';
import type { CardContainerProps } from './types';

const CardContainer: FC<React.PropsWithChildren<CardContainerProps>> = ({
  actions,
  children,
  counter,
  counterDirection = 'right',
  description,
  extendedClassName,
  icon,
  isDark,
  title,
  isCompact,
  isFlat,
  isLmsSendActive,
}) => {
  const wrapper = isDark ? 'bg-base-main' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-base-dark';

  const notification = isDark ? 'bg-gray-lighter-dark-mode' : 'bg-gray-light';
  const showCounterOrZero =
    (counter && Number.isInteger(counter)) || counter === 0;

  const { currentStep, setIsActiveNotificationUser, isActiveNotificationUser } =
    useExcelImporterStepperContext();

  return (
    <div
      className={clsx(
        !isFlat && wrapper,
        'tw-w-full tw-rounded-1.6 tw-border-neutral-light-400 tw-border tw-border-solid',
        extendedClassName,
        !isCompact ? 'tw-py-2.4' : 'tw-pt-2.4',
      )}
    >
      <div
        className={clsx(
          'tw-flex tw-justify-between tw-items-center tw-text-left tw-px-2.4',
          !children && 'tw-mb-2',
        )}
      >
        <div className="tw-flex tw-flex-wrap tw-flex-col tw-w-full">
          <div className="tw-flex tw-items-center tw-h-full">
            {icon && <div className="mr-8">{icon}</div>}
            {showCounterOrZero && counterDirection === 'left' && (
              <div
                className={`${notification} ${textColor} font-caption py-2 px-8 tw-mr-0.8 rounded-5`}
              >
                {counter}
              </div>
            )}
            <p className={`tw-font-subtitle4 mr-8 ${textColor}`}>{title}</p>
            {showCounterOrZero && counterDirection === 'right' && (
              <div
                className={`${notification} ${textColor} font-caption py-2 px-8 rounded-5`}
              >
                {counter}
              </div>
            )}
            {currentStep === 1 && isLmsSendActive && (
              <SwitchNotificationUsers
                isCheckNotification={isActiveNotificationUser?.Massive}
                setIsCheckNotification={e => {
                  setIsActiveNotificationUser &&
                    setIsActiveNotificationUser(prev => ({
                      Manual: prev.Manual,
                      Massive: e,
                    }));
                  customLocalStorage.setItem(
                    'isActiveNotificationUserMassive',
                    JSON.stringify(e),
                  );
                }}
                className="tw-ml-auto"
              />
            )}
          </div>
          {description && (
            <p
              className={clsx(
                isDark
                  ? 'tw-text-neutral-dark-500'
                  : 'tw-text-neutral-light-500',
                'tw-font-body4 tw-mt-0.8 tw-whitespace-pre-line',
              )}
            >
              {description}
            </p>
          )}
        </div>
        {actions}
      </div>
      {children && (
        <div className={clsx('tw-w-full mt-20', !isCompact && 'tw-px-2.4')}>
          {children}
        </div>
      )}
    </div>
  );
};

export default CardContainer;
